import "swiper/css";
import { useTaskCategoryListOptions } from "@/pages/get-don/tasks.api";
import { useSuspenseQuery } from "@tanstack/react-query";
import { ErrorBoundary } from "react-error-boundary";
import { useCategoryStore } from "@/pages/get-don/store/category.store";
import animateHorizontalScroll from "@/shared/hooks/animateHorizontalScroll";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import useHorizontalScroll from "@/shared/hooks/useHorizontalScroll";
import VibratingButton from "@/components/base/button/VibratingButton.tsx";
import { cn } from "@/lib/utils";

// Error handler Component
type ErrorHandlerProps = {
  error: Error;
};

function ErrorHandler(props: ErrorHandlerProps) {
  return <h3 className="text-destructive text-sm">{props.error.message}</h3>;
}

const FilterTab = () => {
  return (
    <ErrorBoundary FallbackComponent={ErrorHandler}>
      <BaseFilterTab />
    </ErrorBoundary>
  );
};

const BaseFilterTab = () => {
  const { data: categoryList } = useSuspenseQuery(useTaskCategoryListOptions());
  const { selectedCategory, setSelectedCategory, hasData } = useCategoryStore();
  const [hasMounted, setHasMounted] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const [activeIndicator, setActiveIndicator] = useState({
    width: 0,
    left: 0,
    opacity: 0,
  });

  useHorizontalScroll(containerRef, undefined, true);

  const TAB_SCROLL_THRESHOLD_PX = 16;
  const SCROLL_DURATION = 300;

  // Update active indicator position and size
  const updateActiveIndicator = useCallback(() => {
    const activeTabElement = tabRefs.current[selectedCategory.idx];
    if (!activeTabElement || !containerRef.current) {
      return;
    }

    const containerRect = containerRef.current.getBoundingClientRect();
    const activeTabRect = activeTabElement.getBoundingClientRect();

    const left =
      activeTabRect.left - containerRect.left + containerRef.current.scrollLeft;
    const width = activeTabRect.width;

    setActiveIndicator({
      width,
      left,
      opacity: 1,
    });
  }, [selectedCategory.idx]);

  // Scroll container to place active tab in the center
  useEffect(() => {
    const container = containerRef.current!;
    const { scrollWidth, offsetWidth, scrollLeft } = container;
    if (scrollWidth <= offsetWidth) {
      return;
    }

    const activeTabElement = container.childNodes[
      selectedCategory.idx
    ] as HTMLElement | null;
    if (!activeTabElement) {
      return;
    }

    const {
      offsetLeft: activeTabOffsetLeft,
      offsetWidth: activeTabOffsetWidth,
    } = activeTabElement;
    const newLeft =
      activeTabOffsetLeft - offsetWidth / 2 + activeTabOffsetWidth / 2;

    // Prevent scrolling by only a couple of pixels, which doesn't look smooth
    if (Math.abs(newLeft - scrollLeft) < TAB_SCROLL_THRESHOLD_PX) {
      return;
    }

    animateHorizontalScroll(container, newLeft, SCROLL_DURATION);
  }, [selectedCategory]);

  useEffect(() => {
    setTimeout(() => {
      setHasMounted(true);
    }, 0);
  }, []);

  // Update active indicator when selected category changes
  useEffect(() => {
    updateActiveIndicator();
  }, [selectedCategory.idx, updateActiveIndicator]);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      updateActiveIndicator();
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [selectedCategory.idx, updateActiveIndicator]);

  // If you need all category, uncomment this line
  // const categoriesWithAll = [{ id: null, name: t("all_tab") }, ...categoryList];
  useLayoutEffect(() => {
    if (categoryList.length > 0 && selectedCategory.id === null) {
      setSelectedCategory({
        id: categoryList[0].id,
        idx: 0,
        isCollection: categoryList[0].is_collection_category,
      });
    }
  }, [categoryList, setSelectedCategory, selectedCategory]);

  return (
    <div
      className="flex flex-nowrap overflow-x-auto items-end overflow-y-hidden relative"
      id="no-scrollbar"
      ref={containerRef}
    >
      {/* Sliding Active Indicator */}
      <div
        className={cn(
          "absolute top-0 h-full bg-white/08 rounded-full border-2 border-transparent pointer-events-none z-0",
          {
            "transition-all duration-300 ease-out": hasMounted,
          }
        )}
        style={{
          width: `${activeIndicator.width}px`,
          transform: `translateX(${activeIndicator.left}px)`,
          opacity: activeIndicator.opacity,
        }}
      />

      {categoryList.map((item, index) => (
        <VibratingButton
          key={index}
          ref={(el) => {
            tabRefs.current[index] = el;
          }}
          className={cn(
            "py-2 px-4 rounded-full border-white/08 shrink-0 text-sm font-medium leading-130 text-gray-1 opacity-50 transition-all duration-300 relative z-10",
            {
              "!border-transparent bg-transparent text-white opacity-100":
                item.id === selectedCategory.id,
              "opacity-50 pointer-events-none": !hasData && item.id !== null,
              "hover:bg-white/04": item.id !== selectedCategory.id && hasData,
            }
          )}
          onClick={() => {
            setSelectedCategory({
              id: item.id,
              idx: index,
              isCollection: item.is_collection_category,
            });
          }}
        >
          {item.name}
        </VibratingButton>
      ))}
    </div>
  );
};

export default FilterTab;
